﻿import { FC, useCallback, useState } from 'react';
import { Loading } from 'components/uikit/suspense/Loading';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';

import styles from './AddEditSignerModal.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { Field } from 'components/uikit/Field.tsx';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { SignerCreateDto } from 'services/api/api-client.types.ts';
import { QueryFactory } from 'services/api';
import { useQueryClient } from '@tanstack/react-query';
import { requiredRule } from 'helpers/form/react-hook-form-helper.ts';
import { FormError } from 'components/uikit/FormError.tsx';

type AddSignerModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

export const AddSignerModal: FC<AddSignerModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.ManageSignerModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();

  const [showOverallErrors, setShowOverallErrors] = useState(false);

  const form = useAdvancedForm<SignerCreateDto>(
    useCallback(async (data) => {
      setShowOverallErrors(true);
      await QueryFactory.SignerClient.create(data);
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.SignerQuery.searchQueryKey(),
      });
      onClose();
    }, []),
  );

  const onClose = () => {
    form.reset();
    form.clearErrors();
    setShowOverallErrors(false);
    props.onClose();
  };

  return (
    <div data-test-id={'add-signer-modal'}>
      <CustomModal
        isOpen={props.isOpen}
        onClose={onClose}
        isBlocking={true}
        title={i18n.t('title_new')}
      >
        <Loading loading={form.formState.isSubmitting}>
          <form onSubmit={form.handleSubmitDefault}>
            <div className={styles.main}>
              <Field
                title={i18n.t('first_name_input')}
                className={styles.field}
              >
                <Input
                  testId={'first-name'}
                  key={'firstName'}
                  {...form.register('firstName', { ...requiredRule() })}
                  errorText={form.formState.errors.firstName?.message}
                />
              </Field>
              <Field title={i18n.t('last_name_input')} className={styles.field}>
                <Input
                  testId={'last-name'}
                  key={'lastName'}
                  {...form.register('lastName', { ...requiredRule() })}
                  errorText={form.formState.errors.lastName?.message}
                />
              </Field>
              <Field title={i18n.t('email_input')} className={styles.field}>
                <Input
                  testId={'email'}
                  key={'email'}
                  {...form.register('email', { ...requiredRule() })}
                  errorText={form.formState.errors.email?.message}
                />
              </Field>
              <FormError>
                {showOverallErrors ? form.overallError : ''}
              </FormError>
            </div>
            <div className={styles.footer}>
              <Button
                testId={'cancel-button'}
                className={styles.button}
                color={ButtonColor.Secondary}
                title={buttonTitle.t('cancel')}
                onClick={onClose}
              />
              <Button
                disabled={!form.formState.isValid}
                testId={'save-button'}
                className={styles.button}
                color={ButtonColor.Primary}
                type={'submit'}
                title={buttonTitle.t('save')}
              />
            </div>
          </form>
        </Loading>
      </CustomModal>
    </div>
  );
};
